import React from 'react';
import { styled, TextField } from '@mui/material';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import { pluralize } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHistory } from 'react-router-dom';
import {
  createEqualisationEditorRoute,
  EQUALISATIONS_PROCESS_CLOSE_SUCCESS,
} from 'adminConstants/routes';
import EqualisationFooter from '../EqualisationFooter';
import { useEqualisationSection } from '../EqualisationContext';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useDisclosure } from 'further-ui/hooks';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import { getQueryKey } from '@trpc/react-query';
import CommitmentsTable from './CommitmentsTable';
import { AddCommitmentsToEqualisationFormValues } from './FormWrapper';
import { Controller, useFormContext } from 'react-hook-form';
import { EQUALISATIONS } from 'adminConstants/routes';
import Button from '@mui/material/Button';

import { Link } from 'react-router-dom';
import { Column } from 'components/Layout';
import FormField from 'further-ui/components/FormField';
import DatePicker from 'further-ui/components/DatePicker';
import EqualisationPage from '../EqualisationPage';

const MaxWidthColumns = styled(Column)(() => ({
  maxWidth: 400,
}));

interface AddCommitmentsToEqualisationFormProps {
  savedEqualisationId?: string;
  isEditingApprovedClose?: boolean;
}

const AddCommitmentsToEqualisationForm: React.FC<
  AddCommitmentsToEqualisationFormProps
> = ({ savedEqualisationId, isEditingApprovedClose }) => {
  const { vehicle } = useLayoutContext();

  const { goToNextSection, goToListPage, nextSection, currentSection } =
    useEqualisationSection();

  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id ?? '');
  const confirmDialog = useDisclosure();

  const [equalisationsData] = api.equalisations.list.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const earliestDateForClose = equalisationsData?.earliestDateForClose;
  const history = useHistory();

  const listEqualisationsQueryKey = getQueryKey(api.equalisations.list, {
    vehicleId: vehicle?._id ?? '',
  });

  const getVehiclesQueryKey = getQueryKey(api.vehicle.listAllVehicles, {
    order: 'desc',
    orderBy: 'vehicleName',
  });

  const commitmentsQueryKey = getQueryKey(api.commitments.list, {
    vehicleId: vehicle?._id ?? '',
    onlyEqualised: true,
  });

  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationActionAllocations,
    {
      equalisationSection: nextSection,
      vehicleId: vehicle?._id ?? '',
      equalisationId: savedEqualisationId ?? '',
    },
  );

  const equalisationQueryKey = getQueryKey(api.equalisations.get, {
    equalisationId: savedEqualisationId ?? '',
    vehicleId: vehicle?._id ?? '',
  });

  const createEqualisation = api.equalisations.create.useMutation(
    useHandleTrpcMutation<{ id?: string }>()({
      onSuccess: (data) => {
        if (equalisationsData.results.length) {
          history.push(createEqualisationEditorRoute(data?.id ?? ''));
        } else {
          history.push(EQUALISATIONS_PROCESS_CLOSE_SUCCESS);
        }
      },
      invalidations: [
        listEqualisationsQueryKey,
        getVehiclesQueryKey,
        commitmentsQueryKey,
      ],
    }),
  );

  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation<{ success: boolean }>()({
      successMessage: isEditingApprovedClose
        ? 'Close updated successfully.'
        : undefined,
      onSuccess: () => {
        if (isEditingApprovedClose) {
          goToListPage();
        } else {
          goToNextSection?.();
        }
      },
      invalidations: [
        equalisationActionAllocationsQueryKey,
        equalisationQueryKey,
        commitmentsQueryKey,
        ...(isEditingApprovedClose ? [listEqualisationsQueryKey] : []),
      ],
    }),
  );

  const { handleSubmit, control, watch, formState } =
    useFormContext<AddCommitmentsToEqualisationFormValues>();

  const allFormCommitments = watch('commitments') ?? [];
  const unEqualisedFormCommitments = isEditingApprovedClose
    ? allFormCommitments
    : allFormCommitments.filter((c) => !c.equalisationDate);

  const onSubmit = (data: AddCommitmentsToEqualisationFormValues) => {
    if (savedEqualisationId && (nextSection || isEditingApprovedClose)) {
      updateEqualisation.mutate({
        equalisationId: savedEqualisationId,
        vehicleId: vehicle?._id ?? '',
        currentSection: isEditingApprovedClose ? currentSection : nextSection!,
        commitments: data.commitments,
        closeDate: data.closeDate,
        name: data.name,
      });
    } else {
      const selectedCommitments = data.commitments.filter((c) => c.isSelected);
      createEqualisation.mutate({
        vehicleId: vehicle?._id ?? '',
        closeDate: data.closeDate,
        name: data.name,
        commitments: selectedCommitments,
      });
    }
  };

  return (
    <EqualisationPage
      subheading={
        unEqualisedFormCommitments.length
          ? isEditingApprovedClose
            ? `This close has ${unEqualisedFormCommitments.length} ${pluralize(
                'commitment',
                unEqualisedFormCommitments.length,
              )}, listed below.`
            : `You have ${unEqualisedFormCommitments.length} ${pluralize(
                'commitment',
                unEqualisedFormCommitments.length,
              )} pending close, listed below.`
          : 'There are no commitments pending close.'
      }
      actionBar={
        <EqualisationFooter
          handleNext={handleSubmit(onSubmit)}
          customNext={
            isEditingApprovedClose ? (
              <Button
                size="large"
                component="button"
                onClick={handleSubmit(onSubmit)}
                loading={updateEqualisation.isPending}
                disabled={!formState.isValid || formState.isSubmitting}
                variant="contained"
              >
                Update close
              </Button>
            ) : savedEqualisationId ? undefined : (
              <Button
                size="large"
                component="button"
                onClick={
                  hasEqualisationInDraftState
                    ? (e) => {
                        e.preventDefault();
                        confirmDialog.onOpen();
                      }
                    : handleSubmit(onSubmit)
                }
                loading={createEqualisation.isPending}
                disabled={!formState.isValid || formState.isSubmitting}
                variant="contained"
              >
                Process close
              </Button>
            )
          }
          customPrevious={
            savedEqualisationId ? undefined : (
              <Button
                size="large"
                component={Link}
                variant="outlined"
                to={EQUALISATIONS}
              >
                Back
              </Button>
            )
          }
          disabled={!formState.isValid || formState.isSubmitting}
          loading={updateEqualisation.isPending}
          hideCompletionPercentage={
            !savedEqualisationId || isEditingApprovedClose
          }
        />
      }
    >
      {unEqualisedFormCommitments.length === 0 ? null : (
        <>
          <MaxWidthColumns spacing="lg">
            <FormField label="Close date" spacing="md">
              <Controller
                name="closeDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    id="close-date"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    required
                    placeholder="Select close date"
                    size="small"
                    minDate={
                      isEditingApprovedClose
                        ? undefined
                        : earliestDateForClose?.toLocalDate()
                    }
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
            <FormField label="Close name" spacing="md">
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    size="small"
                    placeholder="Enter name"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            </FormField>
          </MaxWidthColumns>
          <CommitmentsTable isEditingApprovedClose={isEditingApprovedClose} />
          {hasEqualisationInDraftState && (
            <PendingDraftEqualisationsModal
              isOpen={confirmDialog.isOpen}
              onClose={confirmDialog.onClose}
              onConfirm={handleSubmit(onSubmit)}
              loading={createEqualisation.isPending}
            />
          )}
        </>
      )}
    </EqualisationPage>
  );
};

export default AddCommitmentsToEqualisationForm;
